/**
 * Browser tracing.
 *
 * Posts to the Worker's `/api/telemetry/traces` proxy, never to the collector
 * directly: the collector is behind Cloudflare Access and shipping a service
 * token to the browser would publish it to every user.
 *
 * Unlike the Worker side, batching here is correct and deliberate. A page is
 * long-lived, so accumulating a few spans and flushing on a timer turns dozens
 * of tiny POSTs into one. The "no batching" rule applies to Worker isolates,
 * which are too short-lived for a buffer to survive.
 */

import { exportTraces } from './exporter';
import { shouldSample } from './sampling';
import { formatTraceparent } from './traceparent';
import { Span, Tracer } from './tracer';
import { SpanKind, SpanStatusCode, type Attributes } from './types';

export type BrowserTracingOptions = {
	/** Must be one of the values allowlisted by the telemetry proxy. */
	serviceName: string;
	/** Proxy URL, e.g. `https://api.automadoc.cc/api/telemetry/traces`. */
	endpoint: string;
	/** Head sampling rate, 0..1. */
	sampleRate?: number;
	environment?: string;
	/** App version, if the build exposes one. */
	version?: string;
	/**
	 * Origins that receive a `traceparent` header. Restricted by default —
	 * sending trace headers to a third party leaks internal ids and trips CORS
	 * preflight on any endpoint that does not allow the header.
	 */
	propagateTo?: string[];
	/** Flush interval. */
	flushIntervalMs?: number;
	/** Spans buffered before an early flush. */
	maxBufferedSpans?: number;
};

/**
 * Wall-clock time that is immune to clock adjustments mid-session.
 * `Date.now()` can jump (NTP, user change) and would produce negative or
 * absurd span durations; performance.timeOrigin + performance.now() cannot.
 */
function monotonicNowMs(): number {
	if (typeof performance !== 'undefined' && typeof performance.timeOrigin === 'number') {
		return performance.timeOrigin + performance.now();
	}
	return Date.now();
}

export class BrowserTracing {
	private readonly options: Required<Pick<BrowserTracingOptions, 'sampleRate' | 'flushIntervalMs' | 'maxBufferedSpans'>> &
		BrowserTracingOptions;
	private readonly resource: Attributes;
	private buffer: Tracer[] = [];
	private flushTimer: ReturnType<typeof setTimeout> | null = null;

	constructor(options: BrowserTracingOptions) {
		this.options = {
			sampleRate: 1,
			flushIntervalMs: 5000,
			maxBufferedSpans: 32,
			...options,
		};
		this.resource = {
			'service.name': options.serviceName,
			'service.version': options.version,
			'deployment.environment.name': options.environment,
			'telemetry.sdk.language': 'webjs',
			'browser.language': typeof navigator !== 'undefined' ? navigator.language : undefined,
		};
	}

	/**
	 * Start a trace for one logical user interaction (a route change, a
	 * generation run). Returns undefined when the trace is not sampled, so
	 * callers use optional chaining and pay nothing.
	 */
	startTrace(
		name: string,
		attributes?: Attributes,
		options: { startTimeMs?: number; kind?: SpanKind } = {},
	): Span | undefined {
		const tracer = new Tracer({
			resource: this.resource,
			scopeName: 'automadoc/browser',
			sampled: true,
		});
		if (!shouldSample(tracer.traceId, this.options.sampleRate)) return undefined;

		this.buffer.push(tracer);
		this.scheduleFlush();

		return tracer.startSpan(name, {
			kind: options.kind ?? SpanKind.INTERNAL,
			attributes,
			startTimeMs: options.startTimeMs ?? monotonicNowMs(),
		});
	}

	/** Header value to propagate a span to the backend, or null if unsampled. */
	traceparentFor(span: Span | undefined): string | null {
		return span ? formatTraceparent(span.context) : null;
	}

	/** Whether this URL is allowed to receive trace headers. */
	shouldPropagateTo(url: string): boolean {
		const allowed = this.options.propagateTo;
		if (!allowed || allowed.length === 0) return false;
		try {
			const origin = new URL(url, typeof location !== 'undefined' ? location.href : undefined).origin;
			return allowed.includes(origin);
		} catch {
			return false;
		}
	}

	/**
	 * Record the page load from the Navigation Timing API.
	 *
	 * Deferred until the load event has actually completed. Callers invoke this
	 * during app bootstrap, which runs *before* `load` fires — at that point
	 * `loadEventEnd` is still 0 and the timing record is unusable. Reading it
	 * eagerly silently produced no span at all.
	 *
	 * `loadEventEnd` is only populated once the load event has finished
	 * dispatching, so even inside a `load` listener it can still be 0; the
	 * setTimeout(0) defers past that.
	 */
	recordPageLoad(): void {
		if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function') return;

		if (typeof document !== 'undefined' && document.readyState !== 'complete') {
			window.addEventListener('load', () => setTimeout(() => this.capturePageLoad(), 0), { once: true });
			return;
		}
		this.capturePageLoad();
	}

	private capturePageLoad(): void {
		const [nav] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
		if (!nav || nav.loadEventEnd <= 0) return;

		// The span must cover the real navigation window: it starts at
		// navigation start and ends at loadEventEnd, both of which are already
		// in the past by the time this runs. Defaulting the start to "now"
		// would put it *after* the end.
		const span = this.startTrace(
			'page load',
			{
				'url.path': typeof location !== 'undefined' ? location.pathname : undefined,
				'browser.navigation_type': nav.type,
				// Sub-phases as attributes rather than child spans: they are
				// derived from the same timing record, not independently observed.
				'browser.dns_ms': round(nav.domainLookupEnd - nav.domainLookupStart),
				'browser.tcp_ms': round(nav.connectEnd - nav.connectStart),
				'browser.ttfb_ms': round(nav.responseStart - nav.requestStart),
				'browser.dom_interactive_ms': round(nav.domInteractive),
				'browser.load_event_ms': round(nav.loadEventEnd),
			},
			// nav.startTime is 0 for the navigation entry, so this is timeOrigin.
			{ startTimeMs: performance.timeOrigin + nav.startTime },
		);
		if (!span) return;
		span.setStatus(SpanStatusCode.OK);
		span.end(performance.timeOrigin + nav.loadEventEnd);
	}

	/**
	 * Capture uncaught errors and unhandled rejections as spans.
	 *
	 * This is the closest thing to frontend "logs" available: the collector has
	 * no logs pipeline, so an error becomes a short ERROR span carrying the
	 * exception event. Returns a teardown function.
	 */
	installErrorHandlers(): () => void {
		if (typeof window === 'undefined') return () => {};

		const onError = (event: ErrorEvent) => {
			this.recordError(event.error ?? event.message, {
				'browser.error_source': 'window.onerror',
				'code.filepath': event.filename,
				'code.lineno': event.lineno,
			});
		};
		const onRejection = (event: PromiseRejectionEvent) => {
			this.recordError(event.reason, { 'browser.error_source': 'unhandledrejection' });
		};

		window.addEventListener('error', onError);
		window.addEventListener('unhandledrejection', onRejection);

		return () => {
			window.removeEventListener('error', onError);
			window.removeEventListener('unhandledrejection', onRejection);
		};
	}

	/** Record an error as its own short ERROR span. */
	recordError(error: unknown, attributes?: Attributes): void {
		const span = this.startTrace('browser error', {
			...attributes,
			'url.path': typeof location !== 'undefined' ? location.pathname : undefined,
		});
		if (!span) return;
		span.recordException(error);
		span.end();
		// Errors are worth a prompt flush — the page may be about to unload.
		void this.flush();
	}

	/**
	 * Flush on page hide.
	 *
	 * `visibilitychange`/`pagehide` rather than `unload`: `unload` is not fired
	 * reliably on mobile and blocks the bfcache. sendBeacon is used because a
	 * normal fetch is routinely cancelled during teardown.
	 */
	installFlushOnHide(): () => void {
		if (typeof document === 'undefined') return () => {};

		const onHide = () => {
			if (document.visibilityState === 'hidden') void this.flush(true);
		};
		document.addEventListener('visibilitychange', onHide);
		window.addEventListener('pagehide', () => void this.flush(true));

		return () => document.removeEventListener('visibilitychange', onHide);
	}

	private scheduleFlush(): void {
		const pending = this.buffer.reduce((n, t) => n + t.spanCount, 0);
		if (pending >= this.options.maxBufferedSpans) {
			void this.flush();
			return;
		}
		if (this.flushTimer !== null) return;
		this.flushTimer = setTimeout(() => {
			this.flushTimer = null;
			void this.flush();
		}, this.options.flushIntervalMs);
	}

	/**
	 * Send buffered spans. Tracers with no ended spans are kept for the next
	 * flush so an in-flight interaction is not dropped mid-trace.
	 */
	async flush(useBeacon = false): Promise<void> {
		if (this.flushTimer !== null) {
			clearTimeout(this.flushTimer);
			this.flushTimer = null;
		}

		const ready = this.buffer.filter((t) => t.spanCount > 0);

		// Tracers with no ended spans are kept for the next flush so an in-flight
		// interaction is not dropped mid-trace — but bounded, because a span that
		// never ends (an aborted navigation, a rejected promise on a path that
		// forgets to end its span) would otherwise be retained for the life of the
		// page. Oldest go first: they are the least likely to still complete.
		const pending = this.buffer.filter((t) => t.spanCount === 0);
		this.buffer = pending.slice(-this.options.maxBufferedSpans);

		if (ready.length === 0) return;

		// Merge into one payload: one resource, one scope, all spans.
		const payloads = ready.map((t) => t.toPayload()).filter((p): p is NonNullable<typeof p> => p !== null);
		if (payloads.length === 0) return;

		const spans = payloads.flatMap((p) => p.resourceSpans[0]?.scopeSpans[0]?.spans ?? []);
		if (spans.length === 0) return;

		const merged = {
			resourceSpans: [
				{
					resource: payloads[0]!.resourceSpans[0]!.resource,
					scopeSpans: [{ scope: { name: 'automadoc/browser' }, spans }],
				},
			],
		};

		if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
			try {
				// sendBeacon survives page teardown; it cannot set custom headers,
				// which is fine because the proxy needs none.
				const blob = new Blob([JSON.stringify(merged)], { type: 'application/json' });
				if (navigator.sendBeacon(this.options.endpoint, blob)) return;
			} catch {
				/* fall through to fetch */
			}
		}

		await exportTraces(merged, {
			endpoint: this.options.endpoint,
			// No Access headers: the Worker proxy attaches those server-side.
			onError: () => {
				/* browser telemetry failures are silent by design — never
				   surface an observability problem to a user */
			},
		});
	}

	// No shutdown(): nothing called it. A SPA never tears tracing down, and the
	// install* methods each return their own teardown for the rare caller that
	// needs one — so tracking them a second time here was write-only state.
}

function round(value: number): number | undefined {
	return Number.isFinite(value) && value >= 0 ? Math.round(value) : undefined;
}

/** Collections every frontend addresses by id. */
const COMMON_ID_SEGMENTS: Readonly<Record<string, string>> = {
	templates: ':templateId',
	generations: ':generationId',
	jobs: ':jobId',
};

/**
 * Request path with ids collapsed to placeholders, for use as a span name.
 *
 * Span names must stay low-cardinality or the backing ClickHouse ends up with
 * one distinct name per template — which is both a storage problem and makes
 * every per-endpoint aggregation useless. Shared so no two frontends collapse
 * the same collection different ways; pass `extraSegments` for
 * collections only one app knows about.
 */
export function collapseSpanPath(endpoint: string, extraSegments: Readonly<Record<string, string>> = {}): string {
	let path = endpoint;
	try {
		path = new URL(endpoint, typeof location !== 'undefined' ? location.href : 'http://localhost').pathname;
	} catch {
		/* already a relative path */
	}

	for (const [segment, placeholder] of Object.entries({ ...COMMON_ID_SEGMENTS, ...extraSegments })) {
		path = path.replace(new RegExp(`/${segment}/[^/]+`), `/${segment}/${placeholder}`);
	}
	return path;
}
